import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Copy, Check, ArrowUpRight, Clock, MapPin } from "lucide-react";
import CTABanner from "@/components/CTABanner";
import { Reveal, Eyebrow, MaskLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "APX@apxproject.com";

const serviceOptions = [
  "General Inquiry",
  "NDE Auditing — Level III",
  "VT / MT / PT Inspection",
  "UT / PAUT",
  "Radiographic Film Auditing",
  "QA/QC & Compliance",
  "Field & Project Support",
  "Turnaround Support",
];

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-apx";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "General Inquiry", message: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Contact | APX Project Solutions LLC";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success("Email address copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — the address is " + EMAIL);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received — APX will respond shortly.");
      setForm({ name: "", company: "", email: "", phone: "", service: "General Inquiry", message: "" });
    } catch {
      toast.error("Submission failed — please email us directly at " + EMAIL);
      window.location.href = `mailto:${EMAIL}?subject=Project%20Inquiry%20from%20${encodeURIComponent(form.name)}&body=${encodeURIComponent(form.message)}`;
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="tech-grid relative overflow-hidden border-b border-white/[0.08]" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Eyebrow>Contact APX</Eyebrow>
          <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <MaskLines lines={[<>Talk to a</>, <span className="text-apx">specialist.</span>]} />
          </h1>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={`mailto:${EMAIL}?subject=Project%20Inquiry%20—%20APX%20Project%20Solutions`}
              data-testid="contact-email-cta"
              className="chamfer-sm group inline-flex items-center gap-3 bg-apx px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-white"
            >
              <Mail className="h-4 w-4" />
              {EMAIL}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              onClick={copyEmail}
              data-testid="contact-copy-email"
              className="chamfer-sm inline-flex items-center gap-2 border border-white/20 px-6 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-apx hover:text-apx"
            >
              {copied ? <Check className="h-4 w-4 text-apx" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1.4fr_1fr]" data-testid="contact-main">
        <Reveal>
          <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">Project Inquiry</h2>
          <p className="mt-3 text-sm text-zinc-400">Tell us about your scope, timeline, and standards. A specialist — not a sales queue — reviews every inquiry.</p>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="inquiry-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Full Name *</label>
                <input id="name" data-testid="inquiry-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" />
              </div>
              <div>
                <label htmlFor="company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Company</label>
                <input id="company" data-testid="inquiry-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company LLC" />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Email *</label>
                <input id="email" type="email" data-testid="inquiry-email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Phone</label>
                <input id="phone" data-testid="inquiry-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 000-0000" />
              </div>
            </div>
            <div>
              <label htmlFor="service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Service Interest</label>
              <select id="service" data-testid="inquiry-service" value={form.service} onChange={set("service")} className={inputCls}>
                {serviceOptions.map((o) => (
                  <option key={o} value={o} className="bg-ink">{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Project Details *</label>
              <textarea id="message" data-testid="inquiry-message" required rows={6} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Scope, location, timeline, applicable codes…" />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="inquiry-submit"
              className="chamfer-sm group inline-flex items-center gap-3 bg-apx px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-white disabled:opacity-60"
            >
              {sending ? "Sending…" : "Submit Inquiry"}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <p className="text-xs text-zinc-600">Submissions are stored securely and reviewed by APX. Prefer email? Write to us directly at {EMAIL}.</p>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <aside className="space-y-px overflow-hidden border border-white/[0.08] bg-white/[0.08] lg:sticky lg:top-24" data-testid="contact-info-card">
            <div className="bg-ink p-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Direct Email</span>
              <a href={`mailto:${EMAIL}`} data-testid="contact-info-email" className="mt-3 block font-display text-2xl font-bold uppercase tracking-wide text-white transition-colors hover:text-apx">
                {EMAIL}
              </a>
            </div>
            <div className="bg-ink p-8">
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-apx" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Response Commitment</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Project inquiries are answered within one business day. Urgent field needs and turnaround dispatch — flag it in your subject line and we move faster.
              </p>
            </div>
            <div className="bg-ink p-8">
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-apx" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Coverage</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                APX Project Solutions LLC deploys field teams across the United States — industrial facilities, pipeline right-of-ways, utility sites, and turnaround locations.
              </p>
            </div>
            <div className="bg-ink p-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">What to Include</span>
              <ul className="mt-4 space-y-3">
                {["Project scope & asset type", "Location & mobilization window", "Applicable codes & specs", "Deliverables & reporting needs"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-zinc-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-apx" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </Reveal>
      </section>

      <CTABanner />
    </div>
  );
}
